import type {
  GroupSalesCandidate,
  GroupSalesParticipant,
  GroupSalesSaleItem,
} from '@/utils/approval/groupSales';

/** 단체판매 진행(다중)(131) — 레거시 appr_edit_forms_groupsales_multi */
export const GROUP_SALES_MULTI_TYPE = 131;

export const isGroupSalesMultiType = (apvType: number): boolean =>
  apvType === GROUP_SALES_MULTI_TYPE;

/** 레거시 단체판매 구분 — 판매일이면 금액단체, 판매기간이면 기간단체 */
export type GroupSalesMultiSaleType = '금액' | '기간';

export const GROUP_SALES_MULTI_MAX_ITEMS = 50;

export type GroupSalesMultiItem = {
  seq: string;
  date: string;
  /** 여권 열기 주소. 레거시 문서는 storage 공개 주소 */
  passport: string;
  /** 포탈 파일 서비스 uuid. 레거시 문서는 인트라넷 첨부 번호 */
  passport_no: string;
};

export type GroupSalesMultiShop = {
  shopcode: string;
  shortname: string;
  items: GroupSalesMultiItem[];
};

/** 저장 attributes.group — 레거시 view_json_group_list 와 같은 모양(BE 완료 후처리가 brand·shopcode·seq 를 읽음) */
export type GroupSalesMultiGroup = {
  brand: string;
  name_eng: string;
  vendorCount: number;
  type: GroupSalesMultiSaleType;
  shops: GroupSalesMultiShop[];
};

export type GroupSalesMultiState = {
  group: GroupSalesMultiGroup;
  /** 작성 시점 내역 HTML(펼침 내역 포함) */
  content: string | null;
};

/** 본표 금액 칸 — 레거시 info04(기간)·info05(금액) 첫 행 */
export type GroupSalesMultiAmounts = {
  quantity?: string | null;
  amount?: string | null;
  discountRate?: string | null;
  discountAmount?: string | null;
  saleAmount?: string | null;
};

export type GroupSalesMultiDetail = {
  shopCode: string | null;
  sequence: string | null;
  account: {
    clientName?: string | null;
    contact?: string | null;
    balanceTypeName?: string | null;
    organizationName?: string | null;
  } | null;
  saleItems?: GroupSalesSaleItem[];
  styleCount?: string | null;
  periodSummary?:
    | ({
        businessNumber?: string | null;
        organizationName?: string | null;
        count?: string | null;
      } & GroupSalesMultiAmounts)
    | null;
  /** amountSummary.quantity 로 옮겨지고 제거 예정(PORTAL-15199) */
  amountQuantity?: string | null;
  amountSummary?: GroupSalesMultiAmounts | null;
  participants?: GroupSalesParticipant[];
  /** 이 건만 EAI 조회 실패 시 사유. 나머지 건은 정상으로 옴 */
  error?: unknown;
};

export type GroupSalesMultiDraftSource = {
  code: number;
  attributes: { group?: Partial<GroupSalesMultiGroup> };
  display: {
    candidates: GroupSalesCandidate[] | null;
    items: GroupSalesMultiDetail[] | null;
  };
};

export const GROUP_SALES_MULTI_SAME_TYPE_MESSAGE =
  '동일 유형의 단체판매건을 선택해 주세요.';
export const GROUP_SALES_MULTI_EMPTY_SELECTION_MESSAGE =
  '단체판매 건을 선택해주세요.';
export const GROUP_SALES_MULTI_TOO_MANY_MESSAGE =
  '단체판매 건은 한 번에 {{max}}건까지 선택할 수 있습니다.';
export const GROUP_SALES_MULTI_MISSING_MESSAGE =
  '단체 할인권 발행 정보가 선택되지 않았습니다.';
export const GROUP_SALES_MULTI_DETAIL_ERROR_MESSAGE =
  '내역을 불러오지 못한 단체판매 건이 있어 상신할 수 없습니다. 잠시 후 편집 화면을 다시 열어 저장해 주세요.';
/** 저장 HTML 의 건별 조회 실패 줄 — GroupSalesMulti.tsx 의 오류 칸 class 와 같아야 함 */
export const GROUP_SALES_MULTI_ERROR_CLASS = 'group-sales-error';

export const groupSalesMultiCandidateKey = (c: {
  shopCode?: string | null;
  sequence?: string | null;
}) => `${c.shopCode ?? ''}|${c.sequence ?? ''}`;

/** 레거시 select 핸들러 — 후보 문구로 구분·날짜를 읽음. 문구가 없으면 판정 불가 */
export const parseGroupSalesMultiCandidate = (
  label: string | null | undefined,
): { type: GroupSalesMultiSaleType; date: string } | null => {
  if (!label) return null;
  if (label.includes('판매일'))
    return {
      type: '금액',
      date: (label.split('판매일:')[1] ?? '').split(')')[0],
    };
  return {
    type: '기간',
    date: (label.split('판매기간:')[1] ?? '').split(',')[0],
  };
};

/** 레거시 tagTemplate — 할인권 그룹번호가 있는 문구는 그 번호, 아니면 신청번호 */
export const groupSalesMultiTagNumber = (c: GroupSalesCandidate) => {
  const keyword = '그룹번호 : ';
  if (c.label?.includes(keyword))
    return c.label.split(keyword)[1].split(')')[0];
  return c.sequence ?? '';
};

export type GroupSalesMultiBuildResult =
  | { group: GroupSalesMultiGroup }
  | { error: string };

/**
 * 「확인」 — 고른 순서대로 매장별로 묶음.
 * 레거시는 유형이 섞여도 마지막 건 유형으로 표를 그려 값이 어긋나 섞이면 막음
 */
export const buildGroupSalesMultiGroup = (
  selected: GroupSalesCandidate[],
): GroupSalesMultiBuildResult => {
  if (!selected.length)
    return { error: GROUP_SALES_MULTI_EMPTY_SELECTION_MESSAGE };
  if (selected.length > GROUP_SALES_MULTI_MAX_ITEMS)
    return { error: GROUP_SALES_MULTI_TOO_MANY_MESSAGE };
  const parsed = selected.map((c) => ({
    c,
    meta: parseGroupSalesMultiCandidate(c.label),
  }));
  const types = new Set(parsed.map((p) => p.meta?.type));
  if (types.size > 1 || types.has(undefined))
    return { error: GROUP_SALES_MULTI_SAME_TYPE_MESSAGE };
  const shops: GroupSalesMultiShop[] = [];
  parsed.forEach(({ c, meta }) => {
    const item: GroupSalesMultiItem = {
      seq: c.sequence ?? '',
      date: meta?.date ?? '',
      passport: '',
      passport_no: '',
    };
    const shop = shops.find((s) => s.shopcode === c.shopCode);
    if (shop) shop.items.push(item);
    else
      shops.push({
        shopcode: c.shopCode ?? '',
        shortname: c.shopName ?? '',
        items: [item],
      });
  });
  return {
    group: {
      brand: selected[0].brandCode ?? '',
      name_eng: selected[0].brandNameEn ?? '',
      vendorCount: selected.length,
      type: parsed[0].meta?.type ?? '기간',
      shops,
    },
  };
};

const text = (v: unknown): string =>
  typeof v === 'number' ? String(v) : typeof v === 'string' ? v : '';

const parseJson = (v: unknown): unknown => {
  if (typeof v !== 'string') return v;
  try {
    return JSON.parse(v);
  } catch {
    return null;
  }
};

/** 저장 attributes → 상태. 레거시 문서는 group 이 JSON 문자열일 수 있음 */
export const parseGroupSalesMultiAttributes = (
  attributes: Record<string, unknown> | null | undefined,
): GroupSalesMultiState | null => {
  const raw = parseJson(attributes?.group) as Record<string, unknown> | null;
  if (!raw || typeof raw !== 'object' || !text(raw.brand)) return null;
  const shops = (Array.isArray(raw.shops) ? raw.shops : []).map(
    (s: Record<string, unknown>) => ({
      shopcode: text(s?.shopcode),
      shortname: text(s?.shortname),
      items: (Array.isArray(s?.items) ? s.items : []).map(
        (i: Record<string, unknown>) => ({
          seq: text(i?.seq),
          date: text(i?.date),
          passport: text(i?.passport),
          passport_no: text(i?.passport_no),
        }),
      ),
    }),
  );
  const content = text(attributes?.content);
  return {
    group: {
      brand: text(raw.brand),
      name_eng: text(raw.name_eng),
      vendorCount: Number(raw.vendorCount) || 0,
      type: raw.type === '금액' ? '금액' : '기간',
      shops,
    },
    content: content.trim() ? content : null,
  };
};

export const buildGroupSalesMultiAttributes = (
  state: GroupSalesMultiState | null | undefined,
) => (state ? { group: state.group, content: state.content ?? '' } : null);

/** 레거시는 임시저장·상신 모두 검사 */
export const checkGroupSalesMultiBeforeSave = (
  state: GroupSalesMultiState | null | undefined,
): string | null =>
  state?.group.brand && state.group.vendorCount
    ? null
    : GROUP_SALES_MULTI_MISSING_MESSAGE;

/** 상신만 — 조회 실패 건은 결재자가 내역을 못 본 채 승인돼 완료 처리되므로 막음 */
export const checkGroupSalesMultiBeforeSubmit = (
  state: GroupSalesMultiState | null | undefined,
): string | null =>
  checkGroupSalesMultiBeforeSave(state) ??
  (state?.content?.includes(GROUP_SALES_MULTI_ERROR_CLASS)
    ? GROUP_SALES_MULTI_DETAIL_ERROR_MESSAGE
    : null);

export const groupSalesMultiKey = (
  state: GroupSalesMultiState | null | undefined,
) => (state ? JSON.stringify(state.group) : '');

export const groupSalesMultiItems = (group: GroupSalesMultiGroup) =>
  group.shops.flatMap((shop) =>
    shop.items.map((item) => ({ shopCode: shop.shopcode, sequence: item.seq })),
  );

/** 여권 칸만 바꾼 새 group */
export const updateGroupSalesMultiPassport = (
  group: GroupSalesMultiGroup,
  shopIndex: number,
  itemIndex: number,
  passport: Pick<GroupSalesMultiItem, 'passport' | 'passport_no'>,
): GroupSalesMultiGroup => ({
  ...group,
  shops: group.shops.map((shop, s) =>
    s !== shopIndex
      ? shop
      : {
          ...shop,
          items: shop.items.map((item, i) =>
            i === itemIndex ? { ...item, ...passport } : item,
          ),
        },
  ),
});

export const findGroupSalesMultiDetail = (
  details: GroupSalesMultiDetail[],
  shopCode: string,
  seq: string,
) => details.find((d) => d.shopCode === shopCode && d.sequence === seq) ?? null;

/**
 * 본표 TAG가·할인율·할인금액·실판매가. 기간=info04, 금액=info05.
 * 131 draft-source 는 아직 이 값을 내려주지 않음(BE 요청) — 없으면 레거시처럼 0 으로 보임
 */
export const groupSalesMultiAmounts = (
  detail: GroupSalesMultiDetail | null,
  type: GroupSalesMultiSaleType,
): GroupSalesMultiAmounts =>
  (type === '기간' ? detail?.periodSummary : detail?.amountSummary) ?? {};

/** 금액단체 수량 — amountSummary.quantity, 이전 응답은 amountQuantity */
export const groupSalesMultiAmountQuantity = (
  detail: GroupSalesMultiDetail | null,
) => detail?.amountSummary?.quantity ?? detail?.amountQuantity ?? null;

/** 건별 조회 실패 사유. 빈 값(null·''·0·false·값이 모두 빈 객체)은 정상, 문자열이 아니면 message 를 읽음 */
export const groupSalesMultiDetailError = (
  detail: GroupSalesMultiDetail | null,
): string | null => {
  const error = detail?.error;
  if (!error) return null;
  if (typeof error === 'string') return error.trim() || null;
  if (typeof error === 'object') {
    const values = Object.values(error as Record<string, unknown>);
    if (!values.some((v) => v !== null && v !== undefined && v !== ''))
      return null;
    const message = (error as { message?: unknown }).message;
    return message ? String(message) : '';
  }
  return '';
};
